/**
 * 習慣作成/編集フォームモーダル
 *
 * 習慣の新規作成または既存習慣の編集を行うモーダルダイアログ。
 * モバイルではボトムシート風に、デスクトップでは中央モーダルとして表示。
 *
 * 機能:
 * - 習慣名と説明の入力
 * - 頻度タイプの選択（毎日/曜日指定）
 * - 曜日ごとの実行日設定
 * - カラーピッカー
 *
 * Rails で言えば:
 * - app/views/habits/_form.html.erb + JavaScript に相当
 * - form_with を使ったフォームと同等の機能
 *
 * @example
 * // 新規作成
 * <HabitForm onClose={() => setShowForm(false)} />
 *
 * // 編集
 * <HabitForm habit={editingHabit} onClose={() => setShowForm(false)} />
 */

import { useState } from 'react'
import { router } from '@inertiajs/react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type {
  Habit,
  HabitFormData,
  FrequencyType,
  LoggingEnabledSetting,
} from '@/types'
import { WEEKDAY_LABELS } from '@/types'
import * as Routes from '@/lib/routes'

interface HabitFormProps {
  /** 編集対象の習慣（未指定なら新規作成モード） */
  habit?: Habit
  /** モーダルを閉じるコールバック */
  onClose: () => void
}

const COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
]

const COLOR_NAMES: Record<string, string> = {
  '#3b82f6': '青',
  '#22c55e': '緑',
  '#f59e0b': '黄色',
  '#ef4444': '赤',
  '#8b5cf6': '紫',
  '#ec4899': 'ピンク',
  '#06b6d4': '水色',
  '#f97316': 'オレンジ',
}

export function HabitForm({ habit, onClose }: HabitFormProps) {
  const isEditing = !!habit
  const [processing, setProcessing] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [data, setData] = useState<HabitFormData>({
    name: habit?.name ?? '',
    description: habit?.description ?? '',
    frequencyType: habit?.frequencyType ?? 'daily',
    frequencyDays: habit?.frequencyDays ?? [],
    color: habit?.color ?? COLORS[0],
    loggingEnabled: habit?.loggingEnabledSetting ?? null,
  })

  const updateField = <K extends keyof HabitFormData>(
    key: K,
    value: HabitFormData[K]
  ) => {
    setData((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [key]: _, ...rest } = prev
        return rest
      })
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    const formData = {
      habit: {
        name: data.name,
        description: data.description,
        frequency_type: data.frequencyType,
        frequency_days:
          data.frequencyType === 'weekly' ? data.frequencyDays : [],
        color: data.color,
        logging_enabled: data.loggingEnabled,
      },
    }

    const options = {
      onSuccess: () => onClose(),
      onError: (errors: Record<string, string>) => {
        setErrors(errors)
        setProcessing(false)
      },
      onFinish: () => setProcessing(false),
    }

    if (isEditing && habit) {
      router.patch(Routes.habit_path(habit.id), formData, options)
    } else {
      router.post(Routes.habits_path(), formData, options)
    }
  }

  const toggleWeekday = (day: number) => {
    if (data.frequencyDays.includes(day)) {
      updateField(
        'frequencyDays',
        data.frequencyDays.filter((d) => d !== day)
      )
    } else {
      updateField('frequencyDays', [...data.frequencyDays, day].sort())
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center">
      <div className="bg-card max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl sm:rounded-2xl">
        {/* Header */}
        <div className="border-border bg-card sticky top-0 z-10 flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? '習慣を編集' : '新しい習慣'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 p-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">習慣名 *</Label>
            <Input
              id="name"
              value={data.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="例: 毎日運動する"
              maxLength={100}
              required
              className="touch-target"
            />
            {errors.name && (
              <p className="text-destructive text-sm">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">説明（任意）</Label>
            <Input
              id="description"
              value={data.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="例: 30分以上のウォーキング"
              className="touch-target"
            />
          </div>

          {/* Frequency Type */}
          <div className="space-y-2">
            <Label>頻度</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={data.frequencyType === 'daily' ? 'default' : 'outline'}
                onClick={() =>
                  updateField('frequencyType', 'daily' as FrequencyType)
                }
                className="touch-target flex-1"
              >
                毎日
              </Button>
              <Button
                type="button"
                variant={
                  data.frequencyType === 'weekly' ? 'default' : 'outline'
                }
                onClick={() =>
                  updateField('frequencyType', 'weekly' as FrequencyType)
                }
                className="touch-target flex-1"
              >
                曜日指定
              </Button>
            </div>
          </div>

          {/* Weekday Selection */}
          {data.frequencyType === 'weekly' && (
            <div className="space-y-2">
              <Label>実行する曜日</Label>
              <div className="flex justify-between gap-1">
                {WEEKDAY_LABELS.map((label, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => toggleWeekday(index)}
                    className={cn(
                      'touch-target h-10 w-10 rounded-full text-sm font-medium transition-all',
                      data.frequencyDays.includes(index)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {errors.frequencyDays && (
                <p className="text-destructive text-sm">
                  {errors.frequencyDays}
                </p>
              )}
            </div>
          )}

          {/* Logging */}
          <div className="space-y-2">
            <Label>ログ記録</Label>
            <p className="text-muted-foreground text-xs">
              完了時に時刻・所要時間・メモを記録できます
            </p>
            <div className="flex gap-2">
              {(
                [
                  { value: null, label: 'デフォルト' },
                  { value: true, label: 'ON' },
                  { value: false, label: 'OFF' },
                ] as const
              ).map((opt) => (
                <Button
                  key={String(opt.value)}
                  type="button"
                  variant={
                    data.loggingEnabled === opt.value ? 'default' : 'outline'
                  }
                  onClick={() =>
                    updateField(
                      'loggingEnabled',
                      opt.value as LoggingEnabledSetting
                    )
                  }
                  className="touch-target flex-1"
                  size="sm"
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label>カラー</Label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => updateField('color', color)}
                  className={cn(
                    'touch-target h-10 w-10 rounded-full transition-all',
                    data.color === color && 'ring-primary ring-2 ring-offset-2'
                  )}
                  style={{ backgroundColor: color }}
                  aria-label={COLOR_NAMES[color] || color}
                  title={COLOR_NAMES[color] || color}
                />
              ))}
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="touch-target flex-1"
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              disabled={processing || !data.name}
              className="touch-target flex-1"
            >
              {processing ? '保存中...' : isEditing ? '更新' : '作成'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
